import React, { useState } from "react";
import { useToast } from "../../context/ToastContext";
import ConfirmDialog from "../Shared/ConfirmDialog";

interface GenerateReviewLinkProps {
  selectedPosts: { customer_id: string; post_id: string }[];
  onGenerate: (
    posts: { customer_id: string; post_id: string }[]
  ) => Promise<{
    review_url: string;
    message?: string;
    link_id?: string;
    expires_at?: string;
  }>;
  onClear: () => void;
  /** Revoke a generated link. Only offered when the backend returned a link_id. */
  onRevoke?: (customerId: string, linkId: string) => Promise<void>;
}

const GenerateReviewLink: React.FC<GenerateReviewLinkProps> = ({
  selectedPosts,
  onGenerate,
  onClear,
  onRevoke,
}) => {
  const [reviewLink, setReviewLink] = useState<string | null>(null);
  const [linkId, setLinkId] = useState<string | null>(null);
  const [linkCustomerId, setLinkCustomerId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isRevoked, setIsRevoked] = useState(false);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const { showToast } = useToast();

  const resetLink = () => {
    setReviewLink(null);
    setLinkId(null);
    setLinkCustomerId(null);
    setExpiresAt(null);
    setIsRevoked(false);
  };
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (selectedPosts.length === 0) {
      alert("Please select at least one post to generate a review.");
      return;
    }
    resetLink();
    setIsGenerating(true);
    try {
      const response = await onGenerate(selectedPosts);
      setReviewLink(response.review_url);
      if (response.link_id) {
        setLinkId(response.link_id);
        setLinkCustomerId(selectedPosts[0].customer_id);
        setExpiresAt(response.expires_at ?? null);
      }
    } catch (error) {
      alert("Failed to generate review link. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevoke = () => {
    if (!onRevoke || !linkId || !linkCustomerId) return;
    setRevokeConfirmOpen(true);
  };

  const handleConfirmRevoke = async () => {
    if (!onRevoke || !linkId || !linkCustomerId) return;
    setIsRevoking(true);
    try {
      await onRevoke(linkCustomerId, linkId);
      setIsRevoked(true);
    } catch (error) {
      showToast("Failed to revoke review link. Please try again.", "error");
    } finally {
      setIsRevoking(false);
      setRevokeConfirmOpen(false);
    }
  };

  const expiresLabel = expiresAt
    ? new Date(expiresAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <button
          className="rounded-md bg-theme-primary px-4 py-2 text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating
            ? "Generating..."
            : `Generate Review (${selectedPosts.length})`}
        </button>
        {selectedPosts.length > 0 && (
          <button
            onClick={() => { resetLink(); onClear(); }}
            className="rounded-md border border-theme-border bg-surface-ui px-4 py-2 text-sm text-theme-fg transition-colors hover:bg-surface-ui-hover"
          >
            Clear
          </button>
        )}
      </div>
      {isGenerating && !reviewLink && (
        <div className="rounded-md border border-theme-border bg-theme-background p-4 mt-2">
          <h3 className="text-theme-fg font-medium">
            Generating review link...
          </h3>
          <p className="text-sm text-theme-fg-muted">
            Please wait while we generate your review link.
          </p>
        </div>
      )}
      {reviewLink && (
        <div className="rounded-md border border-theme-border bg-theme-background p-4 mt-2">
          <h3 className="text-theme-fg font-medium mb-1">Review Link Generated!</h3>
          <p className="text-sm text-theme-fg-muted mb-2">
            Share this link with your team or clients to review the selected
            posts:
          </p>
          <div className="flex items-center space-x-2 overflow-x-auto rounded border border-theme-border bg-[var(--color-input-bg)] p-2">
            <a
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`truncate text-brand-primary hover:text-brand-primary-dark${
                isRevoked ? " pointer-events-none line-through opacity-60" : ""
              }`}
              aria-disabled={isRevoked || undefined}
              tabIndex={isRevoked ? -1 : undefined}
            >
              {reviewLink}
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(reviewLink);
                alert("Link copied to clipboard!");
              }}
              className="flex-shrink-0 p-1 bg-surface-ui hover:bg-surface-ui-hover rounded"
              title="Copy to clipboard"
            >
              📋
            </button>
          </div>
          {linkId && (
            <div className="mt-2 flex items-center justify-between gap-2">
              {isRevoked ? (
                <span className="text-sm font-medium text-theme-fg-muted">
                  Revoked
                </span>
              ) : (
                <>
                  {expiresLabel && (
                    <span className="text-sm text-theme-fg-muted">
                      Expires {expiresLabel}
                    </span>
                  )}
                  {onRevoke && (
                    <button
                      onClick={handleRevoke}
                      disabled={isRevoking}
                      className="rounded-md border border-theme-border bg-surface-ui px-3 py-1 text-sm text-theme-fg transition-colors hover:bg-surface-ui-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isRevoking ? "Revoking..." : "Revoke link"}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={revokeConfirmOpen}
        title="Revoke this review link?"
        message="Anyone with the link will no longer be able to view it."
        confirmLabel="Revoke link"
        destructive
        isConfirming={isRevoking}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeConfirmOpen(false)}
      />
    </div>
  );
};

export default GenerateReviewLink;
